import type { ComponentProps, ReactNode } from 'react';
import { cx } from '@/lib/format';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

const base =
  'inline-flex items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed select-none';
const variants: Record<Variant, string> = {
  primary: 'bg-accent text-accent-fg hover:brightness-110',
  secondary: 'bg-bg-elev border border-line-strong hover:bg-bg-sunken',
  ghost: 'hover:bg-bg-sunken text-fg',
  danger: 'bg-danger text-white hover:brightness-110',
};
const sizes: Record<Size, string> = { sm: 'h-7 px-2 text-xs', md: 'h-9 px-3 text-sm' };

export function Button({
  variant = 'secondary',
  size = 'md',
  className,
  children,
  type = 'button',
  ...rest
}: ComponentProps<'button'> & { variant?: Variant; size?: Size; children?: ReactNode }) {
  return (
    <button type={type} className={cx(base, variants[variant], sizes[size], className)} {...rest}>
      {children}
    </button>
  );
}

export function IconButton({
  label,
  className,
  children,
  size = 'md',
  active,
  type = 'button',
  ...rest
}: ComponentProps<'button'> & { label: string; size?: Size; active?: boolean }) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      aria-pressed={active}
      className={cx(
        'inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-bg-sunken hover:text-fg disabled:opacity-40 disabled:cursor-not-allowed',
        size === 'sm' ? 'h-7 w-7' : 'h-9 w-9',
        active && 'bg-accent-soft text-accent',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
